
import { useEffect, useState } from "react";
import { getAllStates, getCitiesByState } from "@/data/estados-cidades";
import { normalizeUrl } from "@/lib/utils";
import SitemapXml from "./SitemapXml";
import { Button } from "./ui/button";
import { toast } from "./ui/use-toast";

interface SitemapLink {
  url: string;
  lastmod: string;
  changefreq: string;
  priority: string;
}

const BASE_URL = "https://www.localup.com.br";
const TODAY = new Date().toISOString().split('T')[0];

const SitemapGenerator = () => {
  const [links, setLinks] = useState<SitemapLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [xmlContent, setXmlContent] = useState("");

  useEffect(() => {
    const generateSitemap = async () => {
      setIsLoading(true);
      
      // Links estáticos
      const staticLinks: SitemapLink[] = [
        { url: `${BASE_URL}/`, lastmod: TODAY, changefreq: "monthly", priority: "1.0" },
        { url: `${BASE_URL}/servicos`, lastmod: TODAY, changefreq: "monthly", priority: "0.8" },
        { url: `${BASE_URL}/sobre`, lastmod: TODAY, changefreq: "monthly", priority: "0.8" },
        { url: `${BASE_URL}/contato`, lastmod: TODAY, changefreq: "monthly", priority: "0.8" },
        { url: `${BASE_URL}/cidades`, lastmod: TODAY, changefreq: "weekly", priority: "0.9" },
      ];
      
      // Links dinâmicos para estados
      const allStates = getAllStates();
      
      const stateLinks: SitemapLink[] = allStates.map(state => ({
        url: `${BASE_URL}/estado/${state.sigla.toLowerCase()}`,
        lastmod: TODAY,
        changefreq: "weekly",
        priority: "0.7"
      }));
      
      // Links dinâmicos para cidades
      let cityLinks: SitemapLink[] = [];
      
      for (const state of allStates) {
        const cities = getCitiesByState(state.sigla);
        const stateLinks = cities.map(city => ({
          url: `${BASE_URL}/cidade/local/${normalizeUrl(city)}`,
          lastmod: TODAY,
          changefreq: "weekly",
          priority: "0.6"
        }));
        
        cityLinks = [...cityLinks, ...stateLinks];
      }
      
      // Combinar todos os links
      const allLinks = [...staticLinks, ...stateLinks, ...cityLinks];
      setLinks(allLinks);
      
      // Gerar o XML
      const xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${allLinks.map(link => `  <url>
    <loc>${link.url}</loc>
    <lastmod>${link.lastmod}</lastmod>
    <changefreq>${link.changefreq}</changefreq>
    <priority>${link.priority}</priority>
  </url>`).join('\n')}
</urlset>`;
      
      setXmlContent(xml);
      setIsLoading(false);
    };
    
    generateSitemap();
  }, []);

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(xmlContent);
    toast({
      title: "Copiado!",
      description: "Conteúdo do sitemap copiado para a área de transferência",
    });
  };
  
  if (isLoading) {
    return <div className="p-8">Gerando sitemap...</div>;
  }
  
  return (
    <div className="sitemap-generator p-8 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Sitemap gerado com {links.length} URLs</h2>
      <p className="mb-6">Este sitemap pode ser baixado e adicionado ao seu servidor ou enviado para o Google Search Console.</p>
      
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <a 
          href={`data:application/xml;charset=utf-8,${encodeURIComponent(xmlContent)}`}
          download="sitemap.xml"
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/80 text-center"
        >
          Download Sitemap.xml
        </a>
        
        <Button 
          onClick={handleCopyToClipboard}
          variant="outline"
        >
          Copiar conteúdo XML
        </Button>
      </div>
      
      <div className="mt-8">
        <h3 className="text-xl font-semibold mb-2">Prévia do sitemap</h3>
        <div className="bg-slate-100 p-4 rounded-md overflow-auto max-h-[400px]">
          <pre className="text-xs">{xmlContent}</pre>
        </div>
      </div>
      
      <div className="mt-8">
        <h3 className="text-xl font-semibold mb-2">Instruções para instalação do sitemap</h3>
        <ol className="list-decimal pl-5 space-y-2">
          <li>Faça o download do arquivo sitemap.xml clicando no botão acima.</li>
          <li>Faça upload do arquivo para a raiz do seu site, de modo que esteja acessível em https://seusite.com/sitemap.xml</li>
          <li>Adicione a seguinte linha ao seu arquivo robots.txt: <code className="bg-slate-200 px-1 rounded">Sitemap: https://seusite.com/sitemap.xml</code></li>
          <li>Envie o sitemap para o Google Search Console e outros mecanismos de busca.</li>
        </ol>
      </div>
    </div>
  );
};

export default SitemapGenerator;
